import { createFileRoute } from '@tanstack/react-router'
import { Home, Settings } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { InviteTeamMemberDialog } from '@/features/team/components/InviteTeamMemberDialog'
import { PendingInvitesTable } from '@/features/team/components/PendingInvitesTable'
import { TeamMembersTable } from '@/features/team/components/TeamMembersTable'
import { teamService } from '@/features/team/services/teamService'
import type {
  PendingInvite,
  TeamMember,
  TeamStats,
} from '@/features/team/types/team.types'
import { PageTitle } from '@/shared/components/common/PageTitle'
import {
  SubtleTabs,
  SubtleTabsContent,
  SubtleTabsList,
  SubtleTabsTrigger,
} from '@/shared/components/common/tabs/SubtleTabs'
import { Badge } from '@/shared/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/shared/components/ui/breadcrumb'
import { useBreadcrumbs } from '@/shared/hooks/useBreadcrumbs'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'

export const Route = createFileRoute('/_auth/settings/team')({
  component: TeamMembersPage,
})

function TeamMembersPage() {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([])
  const [teamStats, setTeamStats] = useState<TeamStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('members')
  const { setBreadcrumbs } = useBreadcrumbs()

  const breadcrumbsContent = useMemo(
    () => (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink to='/dashboard' className='flex items-center gap-1'>
              <Home className='w-4 h-4' />
              Dashboard
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <span className='flex items-center gap-1 text-muted-foreground'>
              <Settings className='w-4 h-4' />
              Settings
            </span>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Team Members</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    ),
    []
  )

  const loadTeamData = useCallback(async () => {
    setLoading(true)
    try {
      const [members, invites, stats] = await Promise.all([
        teamService.getActiveTeamMembers(),
        teamService.getPendingInvites(),
        teamService.getTeamStats(),
      ])
      setTeamMembers(members)
      setPendingInvites(invites)
      setTeamStats(stats)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadTeamData()
  }, [loadTeamData])

  useEffect(() => {
    setBreadcrumbs(breadcrumbsContent)
  }, [setBreadcrumbs, breadcrumbsContent])

  const handleTeamMemberInvited = (newMember: TeamMember) => {
    setTeamMembers(prev => [newMember, ...prev])
    if (teamStats) {
      setTeamStats(prev =>
        prev
          ? {
              ...prev,
              total_members: prev.total_members + 1,
              pending_invites: prev.pending_invites + 1,
            }
          : null
      )
    }
  }

  return (
    <div className='container mx-auto py-6'>
      <div className='space-y-6'>
        {/* Header */}
        <div className='flex flex-col gap-4 md:flex-row md:justify-between md:items-start'>
          <div>
            <PageTitle
              title='Team Members'
              subtitle='Manage team members, roles, and permissions'
            />
            {/* Stats Badges */}
            {teamStats && (
              <div className='flex flex-wrap gap-2 mt-3'>
                <Badge variant='secondary' className='text-sm'>
                  {teamStats.total_members} Total
                </Badge>
                <Badge variant='default' className='text-sm'>
                  {teamStats.active_members} Active
                </Badge>
                <Badge variant='secondary' className='text-sm'>
                  {teamStats.pending_invites} Pending
                </Badge>
              </div>
            )}
          </div>
          <div className='flex gap-3 items-center w-full md:w-auto'>
            <InviteTeamMemberDialog onMemberInvited={handleTeamMemberInvited} />
          </div>
        </div>

        {/* Tables with Subtle Tabs */}
        <SubtleTabs value={activeTab} onValueChange={setActiveTab}>
          <SubtleTabsList>
            <SubtleTabsTrigger value='members'>
              Team Members ({teamMembers.length})
            </SubtleTabsTrigger>
            <SubtleTabsTrigger value='invites'>
              Pending Invites ({pendingInvites.length})
            </SubtleTabsTrigger>
          </SubtleTabsList>

          <SubtleTabsContent value='members'>
            <TeamMembersTable
              teamMembers={teamMembers}
              loading={loading}
              onTeamMemberUpdated={loadTeamData}
              onTeamMemberRemoved={loadTeamData}
            />
          </SubtleTabsContent>

          <SubtleTabsContent value='invites'>
            <PendingInvitesTable
              pendingInvites={pendingInvites}
              loading={loading}
              onInviteResent={loadTeamData}
              onInviteRemoved={loadTeamData}
            />
          </SubtleTabsContent>
        </SubtleTabs>
      </div>
    </div>
  )
}
